/**
 * ThreadTimeline — doctor-only straight-line timeline of a Health Thread.
 *
 * Reads /api/dashboard/health-threads/{id}/timeline and renders visits,
 * medicine changes, tests advised, tests received (lab reports) and
 * milestones as a single chronological event stream. Includes a compact
 * inline editor for the thread's improvement milestones.
 *
 * Legally clean: entirely DpD (doctor's own clinical work-product) —
 * no patient authorization required. Only ever rendered inside doctor
 * dashboard surfaces.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Target, Pill, FlaskConical, CalendarClock, CheckCircle2, XCircle, Plus, Trash2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const KIND_META = {
  visit:              { icon: CalendarClock, dot: 'bg-slate-800',  label: 'Visit' },
  med_change:         { icon: Pill,          dot: 'bg-sky-600',    label: 'Medicine' },
  test_advised:       { icon: FlaskConical,  dot: 'bg-amber-500',  label: 'Test advised' },
  test_received:      { icon: FlaskConical,  dot: 'bg-amber-700',  label: 'Test received' },
  milestone_opened:   { icon: Target,        dot: 'bg-violet-500', label: 'Milestone set' },
  milestone_hit:      { icon: CheckCircle2,  dot: 'bg-emerald-600',label: 'Milestone hit' },
  milestone_missed:   { icon: XCircle,       dot: 'bg-rose-500',   label: 'Milestone missed' },
  milestone_cancelled:{ icon: XCircle,       dot: 'bg-slate-400',  label: 'Milestone cancelled' },
};

const OP_LABEL = { added: '＋ started', stopped: '✕ stopped', up: '↑ up-titrated', down: '↓ down-titrated', held: 'held' };

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso.slice(0, 10); }
}

export default function ThreadTimeline({ threadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Cross-highlight state: hovering a milestone chip highlights every
  // medicine that references it (and vice-versa). Both are milestone-id
  // strings — undefined = no highlight.
  const [hoverMilestoneId, setHoverMilestoneId] = useState(null);
  const [hoverMedName, setHoverMedName] = useState(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/dashboard/health-threads/${threadId}/timeline`);
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Failed to load timeline');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (threadId) load(); }, [threadId]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-stretch sm:items-stretch justify-center p-0 sm:p-3"
      data-testid="thread-timeline-modal"
      onClick={(e) => { /* backdrop is inert — modal is persistent, close only via the X button */ e.stopPropagation(); }}
    >
      <div className="bg-white rounded-none sm:rounded-2xl w-full h-full sm:w-[96vw] sm:h-[94vh] sm:max-w-[1600px] max-h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between z-10">
          <div>
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-emerald-700">Care Progress · Doctor view</p>
            <h3 className="text-xl sm:text-3xl font-extrabold text-slate-900 mt-1" data-testid="thread-timeline-title">
              {data?.thread?.name || 'Care item'}
              {data?.thread?.icd11_code && <span className="ml-2 text-[11px] sm:text-xs font-mono bg-slate-100 text-slate-600 rounded px-2 py-0.5">ICD-11 · {data.thread.icd11_code}</span>}
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {data?.thread?.visit_count || 0} visit(s) · opened {fmtDate(data?.thread?.opened_at)}
            </p>
          </div>
          <button onClick={onClose} className="p-3 rounded-xl hover:bg-slate-100 text-slate-600" data-testid="thread-timeline-close" title="Close">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 px-4 sm:px-8 py-5 sm:py-8 space-y-8">
          {loading && <div className="flex items-center gap-2 text-slate-500 text-base"><Loader2 className="animate-spin" size={18} /> Loading care progress…</div>}
          {err && <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-4 py-3" data-testid="thread-timeline-error">{err}</div>}
          {data && !loading && (
            <>
              {data.lifecycle && (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 sm:px-6 py-3.5 flex items-center justify-between flex-wrap gap-3" data-testid="thread-lifecycle">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${data.lifecycle.is_ongoing ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-700 border border-slate-300'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${data.lifecycle.is_ongoing ? 'bg-emerald-600 animate-pulse' : 'bg-slate-500'}`} />
                      {data.lifecycle.is_ongoing ? 'Ongoing' : (data.lifecycle.status || 'Closed')}
                    </span>
                    <span className="text-sm text-slate-600"><b className="text-slate-800">Started</b> {fmtDate(data.lifecycle.first_visit_at)}</span>
                    <span className="text-sm text-slate-600"><b className="text-slate-800">Latest visit</b> {fmtDate(data.lifecycle.latest_visit_at)}</span>
                    <span className="text-sm text-slate-600"><b className="text-slate-800">{data.lifecycle.age_days || 0}</b> day{data.lifecycle.age_days === 1 ? '' : 's'} tracked</span>
                    <span className="text-sm text-slate-600"><b className="text-slate-800">{data.thread?.visit_count || 0}</b> visit{(data.thread?.visit_count || 0) === 1 ? '' : 's'}</span>
                  </div>
                </div>
              )}
              <MilestonesEditor
                threadId={threadId}
                milestones={data.milestones || []}
                onChanged={load}
                meds={data.medicines_current || []}
                hoverMedName={hoverMedName}
                hoverMilestoneId={hoverMilestoneId}
                setHoverMilestoneId={setHoverMilestoneId}
              />
              <CurrentMedicines
                meds={data.medicines_current || []}
                milestones={data.milestones || []}
                hoverMilestoneId={hoverMilestoneId}
                hoverMedName={hoverMedName}
                setHoverMedName={setHoverMedName}
              />
              <EventStream events={data.events || []} hoverMilestoneId={hoverMilestoneId} />
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 sm:px-8 py-3 text-[11px] sm:text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-bold uppercase tracking-wider text-[10px]">Doctor-only view</span>
          Derived from your own clinical notes · patient sees a curated version in their DHF portal.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Event stream — single straight vertical line
// ─────────────────────────────────────────────────────────────────────────
function EventStream({ events, hoverMilestoneId }) {
  if (!events.length) {
    return (
      <div data-testid="thread-timeline-stream">
        <p className="text-sm sm:text-base font-bold uppercase tracking-wider text-slate-700 mb-4">Care timeline</p>
        <div className="rounded-xl border border-dashed border-slate-200 px-6 py-12 text-center text-base text-slate-500" data-testid="thread-timeline-empty">
          No events yet. Add a milestone or seal a follow-up visit for this care item.
        </div>
      </div>
    );
  }

  // Group events by visit anchor (V1 → V2 → V3). Milestone events that
  // don't belong to a specific visit still render as standalone anchors
  // — they get their own header row so status transitions are visible.
  const visits = [];
  let currentVisit = null;
  const orphans = [];
  events.forEach(ev => {
    if (ev.kind === 'visit') {
      currentVisit = { anchor: ev, children: [] };
      visits.push(currentVisit);
    } else if (currentVisit && (ev.at || '').slice(0, 10) === (currentVisit.anchor.at || '').slice(0, 10)) {
      // Same-day event → tuck under the current visit.
      currentVisit.children.push(ev);
    } else if (currentVisit && ev.kind !== 'milestone_opened' && ev.kind !== 'milestone_hit' && ev.kind !== 'milestone_missed' && ev.kind !== 'milestone_cancelled') {
      currentVisit.children.push(ev);
    } else {
      orphans.push(ev);
    }
  });

  return (
    <div data-testid="thread-timeline-stream">
      <p className="text-sm sm:text-base font-bold uppercase tracking-wider text-slate-700 mb-4">Care timeline · trigger anchored</p>
      <div className="relative pl-8">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300" />
        <ul className="space-y-7">
          {visits.map((v, i) => (
            <VisitAnchor key={i} index={i + 1} anchor={v.anchor} groupedEvents={v.children} hoverMilestoneId={hoverMilestoneId} />
          ))}
          {orphans.map((e, i) => <EventRow key={`orphan-${i}`} e={e} hoverMilestoneId={hoverMilestoneId} />)}
        </ul>
      </div>
    </div>
  );
}

// ─── Visit anchor · groups all same-day events + shows outcome + inference
function VisitAnchor({ index, anchor, groupedEvents = [], hoverMilestoneId }) {
  const p = anchor.payload || {};
  const outcomeMeta = {
    improved: { label: 'Improved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    same:     { label: 'Same',     cls: 'bg-sky-100 text-sky-800 border-sky-300' },
    worse:    { label: 'Worse',    cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    red_flag: { label: '⚠ Red flag triggered', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    cured:    { label: '✓ Cured',  cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  };
  const outMeta = outcomeMeta[p.visit_outcome];
  const isRedFlag = p.visit_outcome === 'red_flag';
  return (
    <li className={`relative pb-2 ${isRedFlag ? '' : ''}`} data-testid={`thread-visit-anchor-${index}`}>
      <span className={`absolute -left-[24px] top-1.5 w-[17px] h-[17px] rounded-full border-2 border-white ring-1 ring-slate-200 ${isRedFlag ? 'bg-rose-600' : 'bg-slate-800'} flex items-center justify-center text-[9px] font-black text-white`}>
        {index}
      </span>
      <div className={`rounded-xl border ${isRedFlag ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200 bg-white'} p-3.5 sm:p-4`}>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Visit {index}</span>
          <span className="text-xs sm:text-sm text-slate-500 font-mono">{fmtDate(anchor.at)}</span>
          {anchor.prescriber_name && <span className="text-xs text-slate-500">· {anchor.prescriber_name}</span>}
          {outMeta && (
            <span className={`ml-auto text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${outMeta.cls}`} data-testid={`thread-visit-outcome-${index}`}>
              {outMeta.label}
            </span>
          )}
        </div>
        {p.reason && (
          <p className="text-sm sm:text-base text-slate-800 mb-1"><span className="font-semibold">Reason ·</span> {p.reason}</p>
        )}
        {p.assessment && (
          <p className="text-sm text-slate-600 mb-1"><span className="font-semibold">Assessment ·</span> {p.assessment}</p>
        )}
        {p.doctor_inference && (
          <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`thread-visit-inference-${index}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Doctor&apos;s observation</p>
            <p className="text-sm text-slate-800 whitespace-pre-line">{p.doctor_inference}</p>
          </div>
        )}
        {isRedFlag && (p.red_flags || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {p.red_flags.map((rf, i) => (
              <span key={i} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200">⚠ {rf}</span>
            ))}
          </div>
        )}
        {p.follow_up_plan && (
          <p className="text-xs text-slate-500 mt-2">→ Follow-up · {p.follow_up_plan}</p>
        )}
        {/* Cause-chain: same-day med changes + test events belong to this visit. */}
        {groupedEvents.length > 0 && (
          <ul className="mt-3 space-y-2 pl-3 border-l-2 border-slate-100">
            {groupedEvents.map((c, i) => <EventRow key={i} e={c} hoverMilestoneId={hoverMilestoneId} inline />)}
          </ul>
        )}
      </div>
    </li>
  );
}

function EventRow({ e, hoverMilestoneId, inline = false }) {
  if (e.kind === 'visit') return null;   // Handled by VisitAnchor.
  const meta = KIND_META[e.kind] || KIND_META.visit;
  const Icon = meta.icon;
  let dim = false;
  let highlight = false;
  if (hoverMilestoneId && e.kind === 'med_change') {
    const linked = (e.payload?.milestone_ids || []).includes(hoverMilestoneId);
    highlight = linked; dim = !linked;
  } else if (hoverMilestoneId && (e.kind === 'milestone_opened' || e.kind === 'milestone_hit' || e.kind === 'milestone_missed' || e.kind === 'milestone_cancelled')) {
    const linked = e.payload?.id === hoverMilestoneId;
    highlight = linked; dim = !linked;
  }
  if (inline) {
    // Compact cause-chain row inside a visit anchor. Drops the timeline
    // dot (anchor already has one) and shrinks vertical rhythm.
    return (
      <li className={`transition ${dim ? 'opacity-40' : ''} ${highlight ? '' : ''}`} data-testid={`thread-event-${e.kind}`}>
        <div className="flex items-start gap-2">
          <Icon size={14} className={`mt-0.5 shrink-0 ${highlight ? 'text-violet-700' : 'text-slate-500'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{meta.label}</span>
            </div>
            <EventBody e={e} />
          </div>
        </div>
      </li>
    );
  }
  return (
    <li className={`relative transition ${dim ? 'opacity-40' : ''}`} data-testid={`thread-event-${e.kind}`}>
      <span className={`absolute -left-[24px] top-1.5 w-[17px] h-[17px] rounded-full border-2 ring-1 ${highlight ? 'border-violet-500 ring-violet-300' : 'border-white ring-slate-200'} ${meta.dot}`} />
      <div className="flex items-start gap-2.5">
        <Icon size={18} className="text-slate-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{meta.label}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs sm:text-sm text-slate-500 font-mono">{fmtDate(e.at)}</span>
            {e.prescriber_name && <span className="text-xs text-slate-500">· {e.prescriber_name}</span>}
          </div>
          <EventBody e={e} />
        </div>
      </div>
    </li>
  );
}

function EventBody({ e }) {
  const p = e.payload || {};
  if (e.kind === 'visit') {
    return <p className="text-sm sm:text-base text-slate-800 mt-1">
      {p.reason || 'Visit logged'}{p.assessment && <span className="text-slate-500"> — {p.assessment}</span>}
    </p>;
  }
  if (e.kind === 'med_change') {
    const opLabel = OP_LABEL[p.op] || p.op;
    return <p className="text-sm sm:text-base text-slate-800 mt-1">
      <span className="font-semibold">{p.name}</span> <span className="text-slate-500">· {opLabel}</span>
      {p.from && p.to && p.op !== 'added' && p.op !== 'stopped' && <span className="text-slate-500"> {p.from} → <b className="text-slate-800">{p.to}</b></span>}
      {p.op === 'added' && p.to && <span className="text-slate-500"> · {p.to}</span>}
    </p>;
  }
  if (e.kind === 'test_advised' || e.kind === 'test_received') {
    return <p className="text-sm sm:text-base text-slate-800 mt-1">
      <span className="font-semibold">{p.test}</span>
      {p.result_summary && <span className="text-slate-500"> — {p.result_summary}</span>}
    </p>;
  }
  if (e.kind === 'milestone_opened') {
    return <p className="text-sm sm:text-base text-slate-800 mt-1">
      <span className="font-semibold">{p.label}</span>
      <span className="text-slate-500"> · target {fmtDate(p.target_at)} ({p.weeks}w)</span>
      {p.signal && <span className="text-slate-500"> · signal: {p.signal}</span>}
    </p>;
  }
  if (e.kind === 'milestone_hit' || e.kind === 'milestone_missed' || e.kind === 'milestone_cancelled') {
    return <p className="text-sm sm:text-base text-slate-800 mt-1">
      <span className="font-semibold">{p.label}</span>
      {p.note && <span className="text-slate-500"> — {p.note}</span>}
    </p>;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
// Current medicines rolling snapshot
// ─────────────────────────────────────────────────────────────────────────
function CurrentMedicines({ meds, milestones = [], hoverMilestoneId, hoverMedName, setHoverMedName }) {
  if (!meds.length) return null;
  const msById = Object.fromEntries((milestones || []).map(m => [m.id, m]));
  return (
    <div>
      <p className="text-sm sm:text-base font-bold uppercase tracking-wider text-slate-700 mb-3">Current medicines on this care item</p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="thread-current-meds">
        {meds.map((m, i) => {
          const medMsIds = m.milestone_ids || [];
          const isHoveredThis = hoverMedName && ((m.brand || m.molecule || '').toLowerCase() === hoverMedName.toLowerCase());
          const isLinkedByMilestone = hoverMilestoneId && medMsIds.includes(hoverMilestoneId);
          const dim = (hoverMilestoneId && !isLinkedByMilestone) || (hoverMedName && !isHoveredThis);
          const highlight = isHoveredThis || isLinkedByMilestone;
          return (
            <li
              key={i}
              onMouseEnter={() => setHoverMedName?.((m.brand || m.molecule || '') || null)}
              onMouseLeave={() => setHoverMedName?.(null)}
              className={`rounded-xl border bg-white px-4 py-3 transition ${highlight ? 'border-violet-400 ring-2 ring-violet-200' : dim ? 'border-slate-100 opacity-40' : 'border-slate-200'}`}
              data-testid={`thread-med-card-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-base sm:text-lg font-semibold text-slate-800">{m.brand || m.molecule || 'Medicine'}</span>
                {m.chronic ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-violet-100 text-violet-800">Chronic · ∞</span>
                ) : m.duration_days > 0 ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-sky-100 text-sky-800">{m.duration_days}d</span>
                ) : null}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {[m.dose, m.frequency, m.duration].filter(Boolean).join(' · ') || '—'}
              </p>
              {m.prescriber_name && <p className="text-xs text-slate-400 mt-1">Rx by {m.prescriber_name} · {fmtDate(m.prescribed_at)}</p>}
              {medMsIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`thread-med-milestones-${i}`}>
                  <span className="text-[10px] uppercase font-bold text-violet-800">Contributes to</span>
                  {medMsIds.map(mid => (
                    <span key={mid} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${hoverMilestoneId === mid ? 'bg-violet-600 text-white border-violet-600' : 'bg-violet-50 text-violet-800 border-violet-200'}`}>
                      🎯 {(msById[mid]?.label || mid).slice(0, 24)}
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Milestones editor (inline)
// ─────────────────────────────────────────────────────────────────────────
function MilestonesEditor({ threadId, milestones, onChanged, meds = [], hoverMedName, setHoverMilestoneId }) {
  const [rows, setRows] = useState(() => milestones.map(m => ({ ...m })));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setRows(milestones.map(m => ({ ...m }))); }, [milestones]);

  const dirty = useMemo(() => JSON.stringify(rows.map(r => ({ l: r.label, w: r.weeks, s: r.signal }))) !== JSON.stringify(milestones.map(m => ({ l: m.label, w: m.weeks, s: m.signal }))), [rows, milestones]);

  const addRow = () => setRows(r => [...r, { label: '', weeks: 2, signal: '', status: 'open' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updRow = (i, patch) => setRows(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = rows.filter(r => (r.label || '').trim()).map(r => ({
        label: r.label.trim(),
        weeks: Math.max(1, Math.min(26, parseInt(r.weeks, 10) || 2)),
        signal: (r.signal || '').trim(),
      }));
      await api.post(`/dashboard/health-threads/${threadId}/milestones`, payload);
      await onChanged();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggle = async (m, next) => {
    try {
      await api.patch(`/dashboard/health-threads/${threadId}/milestones/${m.id}`, { status: next });
      await onChanged();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Update failed'); }
  };

  return (
    <div className="rounded-2xl border-2 border-dashed border-violet-300 bg-violet-50/30 p-4 sm:p-6" data-testid="thread-milestones-editor">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Target size={18} className="text-violet-700" />
          <p className="text-sm sm:text-base font-bold uppercase tracking-widest text-violet-800">Expected Improvement Milestones</p>
          <span className="text-xs text-slate-500">Doctor-authored · patient sees a curated version</span>
        </div>
        <button onClick={addRow} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-violet-300 text-violet-800 text-sm font-semibold hover:bg-violet-50" data-testid="thread-milestone-add">
          <Plus size={14} /> Add milestone
        </button>
      </div>

      {!rows.length && (
        <p className="text-sm text-slate-500 italic px-1 py-3">Set 1–4 target outcomes for this care item. Each carries a 1–26 week timeline.</p>
      )}

      <ul className="space-y-3">
        {rows.map((r, i) => {
          // Which meds contribute to this milestone?
          const linkedMeds = (meds || []).filter(md => (md.milestone_ids || []).includes(r.id));
          const linkedNamesLower = linkedMeds.map(md => (md.brand || md.molecule || '').toLowerCase());
          const highlighted = hoverMedName && linkedNamesLower.includes(hoverMedName.toLowerCase());
          const dim = hoverMedName && !highlighted;
          return (
          <li
            key={r.id || i}
            onMouseEnter={() => r.id && setHoverMilestoneId?.(r.id)}
            onMouseLeave={() => setHoverMilestoneId?.(null)}
            className={`rounded-xl bg-white border px-4 py-3 sm:px-5 sm:py-4 transition ${highlighted ? 'border-violet-500 ring-2 ring-violet-200' : dim ? 'border-slate-100 opacity-40' : 'border-violet-200'}`}
            data-testid={`thread-milestone-row-${i}`}>
            <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_auto_1.2fr_auto_auto] gap-3 lg:items-center">
              <input
                type="text" value={r.label || ''} onChange={e => updRow(i, { label: e.target.value })}
                placeholder="e.g. BP down to < 140/90"
                className="rounded-xl border border-slate-300 px-3.5 py-2.5 sm:py-3 text-base sm:text-lg focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid={`thread-milestone-label-${i}`} />
              <div className="flex gap-1.5 flex-wrap items-center">
                {[1,2,3,4,5,6].map(w => (
                  <button key={w} type="button" onClick={() => updRow(i, { weeks: w })}
                    className={`text-sm font-semibold px-3 py-2 rounded-lg border ${r.weeks === w ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-violet-50'}`}
                    data-testid={`thread-milestone-week-${i}-${w}`}>{w}w</button>
                ))}
                <input type="number" min="1" max="26" value={![1,2,3,4,5,6].includes(r.weeks) ? r.weeks : ''}
                  onChange={e => updRow(i, { weeks: parseInt(e.target.value || '2', 10) })}
                  placeholder="custom"
                  className="w-24 rounded-lg border border-slate-300 px-2.5 py-2 text-sm" />
              </div>
              <input
                type="text" value={r.signal || ''} onChange={e => updRow(i, { signal: e.target.value })}
                placeholder="signal (e.g. lab upload, patient tap)"
                className="rounded-xl border border-slate-300 px-3.5 py-2.5 sm:py-3 text-sm sm:text-base text-slate-700 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid={`thread-milestone-signal-${i}`} />
              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md text-center whitespace-nowrap ${r.status === 'hit' ? 'bg-emerald-100 text-emerald-800' : r.status === 'missed' ? 'bg-rose-100 text-rose-800' : r.status === 'cancelled' ? 'bg-slate-100 text-slate-600' : 'bg-violet-100 text-violet-800'}`}>
                {r.status || 'open'}
              </span>
              <div className="flex items-center gap-1.5 justify-end flex-wrap">
                {r.id && (r.status === 'open' || !r.status) && (
                  <>
                    <button onClick={() => toggle(r, 'hit')} className="text-xs px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 font-semibold" data-testid={`thread-milestone-hit-${i}`}>Hit</button>
                    <button onClick={() => toggle(r, 'missed')} className="text-xs px-3 py-1.5 rounded-md bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 font-semibold" data-testid={`thread-milestone-miss-${i}`}>Miss</button>
                  </>
                )}
                {r.id && (r.status === 'hit' || r.status === 'missed') && (
                  <button onClick={() => toggle(r, 'open')} className="text-xs px-3 py-1.5 rounded-md bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 font-semibold">Reopen</button>
                )}
                <button onClick={() => removeRow(i)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500" title="Remove" data-testid={`thread-milestone-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {r.target_at && (
              <p className="text-xs text-slate-500 mt-2 pl-1">Target · {fmtDate(r.target_at)}{r.closed_at && ` · closed ${fmtDate(r.closed_at)}`}</p>
            )}
            {linkedMeds.length > 0 && (
              <p className="text-xs text-violet-800 mt-2 pl-1 flex items-center gap-1 flex-wrap" data-testid={`thread-milestone-driven-by-${i}`}>
                <span className="text-[10px] font-bold uppercase tracking-wider">Driven by</span>
                {linkedMeds.map((md, mi) => (
                  <span key={mi} className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200">
                    💊 {md.brand || md.molecule || 'medicine'}
                  </span>
                ))}
              </p>
            )}
          </li>
        );
        })}
      </ul>

      {(dirty || err) && (
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <p className="text-sm text-rose-600">{err}</p>
          <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-violet-700" data-testid="thread-milestone-save">
            {saving ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null} Save milestones
          </button>
        </div>
      )}
    </div>
  );
}
